import type { KPIData } from "@/shared/models/KpiData";
import type { ShiftIncentiveData } from "@/pages/incentive/models/ShiftIncentiveData";

export interface ShiftReportStats {
	ShiftId: number;
	ShiftName: string;
	BilletTons: number;
	BilletPcs: number;
	RolledTons: number;
	FinishedTons: number;
	FinishedBundles: number | null;
	MillYield: number | null;
	FinishedYield: number | null;
	TotalCobbles: number;
	TotalAdjustments: number;
	Production: number | null;
	Delays: number | null;
	GapTime: number | null;
	TPH: number | null;
	TargetTPH: number | null;
	BudgetTPH: number | null;
	HoursActual: number;
	AvgPushTimeSecondsCurrent: number | null;
	AvgPushTimeSecondsPrevious: number | null;
	MinPushTimeSeconds: number | null;
	MaxPushTimeSeconds: number | null;
}

export interface KPIThresholds {
	Red: number;
	Orange: number;
	Yellow: number;
	GreenSoft: number;
	GreenNormal: number;
	GreenHard: number;
}

export interface ShiftReportProduction {
	EventTypeCode: string;
	SectionId: number;
	SectionName: string;
	HeatId: number;
	HeatName: string;
	Billets: number;
	Weight: number;
	Start: Date | null;
	End: Date | null;
	Duration: number;
	TPH: number;
	PushRate: number;
}

export interface ShiftReportGanttDelay {
	DelayId: number;
	Start: Date | null;
	End: Date | null;
	Duration: number;
	DelayCodeName: string;
	DelayCategoryName: string | null;
	DelayCategoryColor: string | null;
}

export interface ShiftReportHeat {
	CampaignId: number | null;
	HeatId: number | null;
	HeatName: string | null;
	SectionId: number | null;
	SectionName: string | null;
	Start: Date | null;
	End: Date | null;
	Duration: number | null;
	CampaignDuration: number | null;
	ChargedBillets: number | null;
	ChargedColdTons: number | null;
	RolledBillets: number | null;
	RolledColdTons: number | null;
	RejectedBillets: number | null;
	RejectedColdTons: number | null;
	Cobbles: number | null;
	TonsPerHour: number | null;
	// Campaign totals (for boundary heats display)
	CampaignChargedBillets: number | null;
	CampaignChargedColdTons: number | null;
	CampaignRolledBillets: number | null;
	CampaignRolledColdTons: number | null;
	CampaignCobbles: number | null;
}

export interface ShiftReportDelay {
	DelayId: number;
	Start: Date | null;
	End: Date | null;
	Duration: number;
	DelayCodeName: string;
	DelayCodePath: string;
	DelayCategoryName: string | null;
	DelayCategoryColor: string | null;
	Comments: string;
}

export interface ShiftReportProductionSummary {
	SectionId: number;
	SectionName: string;
	HeatsCount: number;
	Start: Date | null;
	End: Date | null;
	Duration: number;
	CampaignDuration: number;
	ChargedBillets: number;
	ChargedColdTons: number;
	RolledBillets: number;
	RolledColdTons: number;
	RejectedBillets: number;
	RejectedColdTons: number;
	Cobbles: number;
	AvgTonsPerHour: number;
	// Campaign totals (for boundary heats display)
	CampaignChargedBillets: number;
	CampaignChargedColdTons: number;
	CampaignRolledBillets: number;
	CampaignRolledColdTons: number;
	CampaignCobbles: number;
}

export interface RollingTimeData {
	TargetedRollingTimeSecs: number | null;
	ExpectedRollOutTime: Date | null;
	ScheduledRollOutTime: Date | null;
	TimeLeftTillRollChangeSecs: number | null;
	TimeLeftColor:
		| "red"
		| "orange"
		| "yellow"
		| "greenSoft"
		| "greenNormal"
		| "greenHard";
	ElapsedRollingTimeSecs: number | null;
	ChangeoverTimeSecs: number | null;
	CurrentSectionName: string | null;
	NextSectionName: string | null;
	ScheduledRollChangeHours: number | null;
}

export interface CarrierChangeData {
	CarrierInstanceId: number;
	StandName: string;
	StandSortOrder: number;
	CarrierName: string | null;
	SectionId: number | null;
	SectionName: string | null;
	RollsetLetter: string | null;
	TopRollName: string | null;
	BottomRollName: string | null;
	SelectedPass: number | null;
	SelectedPassTons?: number | null;
	InstalledAt: Date | null;
	UninstalledAt: Date | null;
	CarrierTons?: number | null;
}

export interface PassChangeData {
	CarrierInstanceId: number;
	StandName: string;
	StandSortOrder: number;
	CarrierName: string | null;
	SectionId: number | null;
	SectionName: string | null;
	TopRollName: string | null;
	BottomRollName: string | null;
	PreviousPass: number | null;
	CurrentPass: number;
	Timestamp: Date | null;
	InstalledAt: Date | null;
	UninstalledAt: Date | null;
	PreviousPassTons?: number | null;
}

export interface ShiftReportRollChange {
	EventId: number;
	PrevSectionName: string;
	NextSectionName: string;
	Start: Date | null;
	End: Date | null;
	ScheduledRollChangeHours: number;
	ActualRollChangeHours: number | null;
	TrackedRollChangeHours?: number | null;
	EffectiveActualRollChangeHours?: number | null;
	IsActualRollChangeEdited?: boolean | number | null;
	StatusCode: string;
	RemainingMinutes: number | null;
}

export interface DmhEvent {
	EventId: number;
	EventTypeCode: string;
	Start: Date | null;
	End: Date | null;
}

export interface ShiftReportStackerBundle {
	GroupKey: string;
	ShiftId: number;
	SectionId: number;
	SectionName: string;
	GradeId: number | null;
	GradeName: string | null;
	HeatId: number | null;
	HeatName: string | null;
	LengthInches: number;
	GoodBundles: number;
	GoodPiecesPerBundle: number;
	TotalPieces: number;
	TotalWeight: number | null;
	ScrapPieces: number | null;
	ScrapWeight: number | null;
	ScrapReason: string | null;
	BundlesBreakdown: string | null;
	// Bundles nobody reviewed yet on Stacker Production: heat to check / detected with the mill stopped
	HeatSuspiciousBundles: number | null;
	PendingBundles: number | null;
}

// Bundles of the shift still to review on Stacker Production (the Finished numbers may change)
export const stackerPendingReview = (bundles: ShiftReportStackerBundle[]) => {
	const heat = bundles.reduce((s, b) => s + (b.HeatSuspiciousBundles ?? 0), 0);
	const millStopped = bundles.reduce((s, b) => s + (b.PendingBundles ?? 0), 0);
	return { heat, millStopped, total: heat + millStopped };
};

export interface PushRateCrewData {
	Crew: string;
	Shift: string | null;
	PushRate: number | null;
	PushRatePct: number | null;
	ShiftStart: string | null;
	ShiftEnd: string | null;
	ShiftId: number | null;
	IsCurrentCrew: boolean;
	SectionName: string | null;
}

export interface ShiftReport {
	ShiftStats: ShiftReportStats | null;
	KpiData: KPIData | null;
	GanttProduction: ShiftReportProduction[];
	GanttDelays: ShiftReportGanttDelay[];
	Heats: ShiftReportHeat[];
	Delays: ShiftReportDelay[];
	ProductionSummary: ShiftReportProductionSummary[];
	RollingTime: RollingTimeData | null;
	IncentiveData: ShiftIncentiveData | null;
	UninstalledCarriers: CarrierChangeData[];
	InstalledCarriers: CarrierChangeData[];
	PassChanges: PassChangeData[];
	RollChangeIntervals: ShiftReportRollChange[];
	DmhEvents: DmhEvent[];
	StackerBundles: ShiftReportStackerBundle[];
	PushRateData: PushRateCrewData[];
}

export interface ShiftReportResponse {
	errorMessage: null;
	returnValue?: number;
	tables: {
		0: { rows: ShiftReportStats[] };
		1: { rows: KPIData[] };
		2: { rows: ShiftReportProduction[] };
		3: { rows: ShiftReportGanttDelay[] };
		4: { rows: ShiftReportHeat[] };
		5: { rows: ShiftReportDelay[] };
		6: { rows: ShiftReportProductionSummary[] };
		7: { rows: RollingTimeData[] };
		8: { rows: CarrierChangeData[] };
		9: { rows: CarrierChangeData[] };
		10: { rows: PassChangeData[] };
		11: { rows: ShiftReportRollChange[] };
		12: { rows: DmhEvent[] };
		13: { rows: ShiftIncentiveData[] };
		14: { rows: ShiftReportStackerBundle[] };
		15: { rows: PushRateCrewData[] };
	};
}
